import { createTheme } from '@mui/material/styles';

export const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#3b82f6', // Blue
      light: '#60a5fa',
      dark: '#1d4ed8',
    },
    secondary: {
      main: '#8b5cf6', // Purple
      light: '#a78bfa',
      dark: '#7c3aed',
    },
    success: {
      main: '#10b981', // Green
      light: '#34d399',
      dark: '#059669',
    },
    warning: {
      main: '#f59e0b', // Orange
      light: '#fbbf24',
      dark: '#d97706',
    },
    error: {
      main: '#ef4444', // Red
      light: '#f87171',
      dark: '#dc2626',
    },
    background: {
      default: '#111827', // Gray-900
      paper: '#1f2937', // Gray-800
    },
    text: {
      primary: '#f9fafb', // Gray-50
      secondary: '#9ca3af', // Gray-400
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
    },
    h2: {
      fontWeight: 600,
    },
    h3: {
      fontWeight: 600,
    },
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 16,
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          borderRadius: 24,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          textTransform: 'none',
          fontWeight: 600,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 16,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiInputLabel-root': {
            zIndex: 1,
            backgroundColor: 'transparent',
            position: 'absolute',
            top: '50%',
            left: '14px',
            transform: 'translateY(-50%)',
            transition: 'all 0.2s ease-in-out',
            pointerEvents: 'none',
            '&.Mui-focused, &.MuiFormLabel-filled': {
              top: '0px',
              transform: 'translate(14px, -9px) scale(0.75)',
              backgroundColor: '#1f2937',
              padding: '2px 8px',
              borderRadius: '4px',
              zIndex: 2,
            },
          },
          '& .MuiOutlinedInput-root': {
            position: 'relative',
            zIndex: 0,
            overflow: 'visible',
            '& .MuiInputAdornment-root': {
              zIndex: 10,
              position: 'relative',
              '&.MuiInputAdornment-positionEnd': {
                marginRight: '8px',
              },
            },
            '& .MuiInputBase-input': {
              zIndex: 1,
            },
            '&.MuiInputBase-adornedStart .MuiInputLabel-root': {
              left: '48px',
              '&.Mui-focused, &.MuiFormLabel-filled': {
                left: '14px',
                transform: 'translate(14px, -9px) scale(0.75)',
              },
            },
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          zIndex: 1,
          position: 'absolute',
          top: '50%',
          left: '14px',
          transform: 'translateY(-50%)',
          transition: 'all 0.2s ease-in-out',
          pointerEvents: 'none',
          '&.Mui-focused, &.MuiFormLabel-filled': {
            top: '0px',
            transform: 'translate(14px, -9px) scale(0.75)',
            backgroundColor: '#1f2937',
            padding: '2px 8px',
            borderRadius: '4px',
            zIndex: 2,
          },
        },
        outlined: {
          '&.MuiInputLabel-sizeSmall': {
            transform: 'translate(14px, 9px) scale(1)',
            '&.MuiInputLabel-shrink': {
              transform: 'translate(14px, -9px) scale(0.75)',
            },
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          overflow: 'visible',
          '& .MuiInputAdornment-root': {
            zIndex: 10,
            position: 'relative',
            '& .MuiIconButton-root': {
              zIndex: 10,
              position: 'relative',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
              },
            },
            '&.MuiInputAdornment-positionEnd': {
              marginRight: '8px',
            },
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          '& .MuiInputLabel-root': {
            zIndex: 1,
            '&.Mui-focused, &.MuiFormLabel-filled': {
              backgroundColor: '#1f2937',
              padding: '0 8px',
              borderRadius: '4px',
            },
          },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        root: {
          zIndex: 1400,
        },
        paper: {
          zIndex: 1400,
          maxHeight: '300px',
          overflow: 'auto',
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        root: {
          zIndex: 1400,
        },
        paper: {
          zIndex: 1400,
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          position: 'relative',
          '& .MuiInputLabel-root': {
            zIndex: 1,
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          zIndex: 1500,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        root: {
          zIndex: 1300,
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          position: 'relative',
          zIndex: 1,
          '&.Mui-expanded': {
            zIndex: 2,
          },
        },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          zIndex: 1200,
        },
      },
    },
  },
});

export interface Theme {
  colors: {
    primary: string;
    primaryHover: string;
    secondary: string;
    background: string;
    surface: string;
    surfaceHover: string;
    text: {
      primary: string;
      secondary: string;
      tertiary: string;
      inverse: string;
    };
    border: {
      primary: string;
      secondary: string;
    };
    accent: {
      blue: string;
      blueLight: string;
      blueBorder: string;
      green: string;
      greenLight: string;
      greenBorder: string;
      orange: string;
      orangeLight: string;
      orangeBorder: string;
      purple: string;
      purpleLight: string;
      purpleBorder: string;
      yellow: string;
      yellowLight: string;
      yellowBorder: string;
    };
    gradient: string;
  };
  spacing: {
    xs: string;
    sm: string;
    md: string;
    lg: string;
    xl: string;
  };
  borderRadius: {
    sm: string;
    md: string;
    lg: string;
    xl: string;
    full: string;
  };
}

export const darkTheme: Theme = {
  colors: {
    primary: '#3b82f6',
    primaryHover: '#2563eb',
    secondary: '#6b7280',
    background: '#111827',
    surface: '#1f2937',
    surfaceHover: '#374151',
    text: {
      primary: '#f9fafb',
      secondary: '#d1d5db',
      tertiary: '#9ca3af',
      inverse: '#111827',
    },
    border: {
      primary: '#374151',
      secondary: '#4b5563',
    },
    accent: {
      blue: '#3b82f6',
      blueLight: '#1e3a8a',
      blueBorder: '#1d4ed8',
      green: '#10b981',
      greenLight: '#064e3b',
      greenBorder: '#047857',
      orange: '#f59e0b',
      orangeLight: '#92400e',
      orangeBorder: '#d97706',
      purple: '#8b5cf6',
      purpleLight: '#581c87',
      purpleBorder: '#7c3aed',
      yellow: '#fbbf24',
      yellowLight: '#92400e',
      yellowBorder: '#d97706',
    },
    gradient: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
  },
  spacing: {
    xs: '0.5rem',
    sm: '0.75rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
  },
  borderRadius: {
    sm: '0.375rem',
    md: '0.5rem',
    lg: '0.75rem',
    xl: '1rem',
    full: '9999px',
  },
};

export const lightTheme: Theme = {
  colors: {
    primary: '#3b82f6',
    primaryHover: '#2563eb',
    secondary: '#6b7280',
    background: '#f8fafc',
    surface: '#ffffff',
    surfaceHover: '#f3f4f6',
    text: {
      primary: '#111827',
      secondary: '#374151',
      tertiary: '#6b7280',
      inverse: '#ffffff',
    },
    border: {
      primary: '#e5e7eb',
      secondary: '#d1d5db',
    },
    accent: {
      blue: '#3b82f6',
      blueLight: '#eff6ff',
      blueBorder: '#bfdbfe',
      green: '#10b981',
      greenLight: '#f0fdf4',
      greenBorder: '#bbf7d0',
      orange: '#f59e0b',
      orangeLight: '#fff7ed',
      orangeBorder: '#fed7aa',
      purple: '#8b5cf6',
      purpleLight: '#faf5ff',
      purpleBorder: '#e9d5ff',
      yellow: '#fbbf24',
      yellowLight: '#fefce8',
      yellowBorder: '#fef08a',
    },
    gradient: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
  },
  spacing: {
    xs: '0.5rem',
    sm: '0.75rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
  },
  borderRadius: {
    sm: '0.375rem',
    md: '0.5rem',
    lg: '0.75rem',
    xl: '1rem',
    full: '9999px',
  },
};

// Current theme - change this to switch themes
export const currentTheme = darkTheme; 